export interface ConversationListItem {
  id: string
  title: string
  createdAt: string
  updatedAt: string
  /** Server-side continuation is enabled for this conversation (opt-in, billed like any turn). */
  cloudContinue?: boolean
  /**
   * A turn was added that this client hasn't loaded — from another device, or from the agent
   * continuing on its own. Cleared when the conversation is opened or refreshed.
   */
  hasUpdates?: boolean
  /**
   * Added by a send on this client before the server created the row (it does on RUN_STARTED).
   * Such a conversation cannot be renamed yet — the PATCH would 404.
   */
  unsaved?: boolean
}

/** Flag/unflag a conversation as having turns this client hasn't seen. */
export function markConversationUpdated(
  prev: ConversationListItem[],
  id: string,
  hasUpdates: boolean,
): ConversationListItem[] {
  let changed = false
  const next = prev.map((c) => {
    if (c.id !== id || Boolean(c.hasUpdates) === hasUpdates) return c
    changed = true
    return {...c, hasUpdates}
  })
  return changed ? next : prev
}

/** Placeholder title until the generated one arrives; same as the server's (first 50 chars). */
export function titleFromUserMessage(text: string, maxLen = 50): string {
  return text.trim().slice(0, maxLen).trim() || 'New conversation'
}

/** Default title on a newly constructed session controller — not a persisted conversation name. */
const UNSET_SESSION_TITLES = new Set(['', 'untitled', 'new chat', 'new conversation'])

/** True when the session tab still has the placeholder name, not a real conversation title. */
export function isUnsetConversationTitle(title?: string | null): boolean {
  if (title == null) return true
  return UNSET_SESSION_TITLES.has(title.trim().toLowerCase())
}

/**
 * Tab / thread label. The history-list name is the single source of truth, so the tab and the history
 * always show the same title (a rename in either place updates both). A session title is only a fallback
 * for a draft that is not in the list yet.
 */
export function resolveConversationTabTitle(
  listTitle?: string | null,
  sessionTitle?: string | null,
  fallback = 'New chat',
): string {
  const fromList = listTitle?.trim()
  if (fromList) return fromList
  const session = sessionTitle?.trim()
  if (session && !isUnsetConversationTitle(session)) return session
  return fallback
}

/** Prepend or update a conversation in the sidebar list (newest first). */
export function upsertConversationInList(
  prev: ConversationListItem[],
  item: ConversationListItem,
): ConversationListItem[] {
  const without = prev.filter((c) => c.id !== item.id)
  return [item, ...without]
}

/**
 * A turn happened: bump ``updatedAt`` and move the conversation to the top (the history is ordered by
 * last activity). ``placeholderTitle`` (the first prompt) only names a conversation that is new to the
 * list or still unnamed — an existing title, generated or renamed, is never replaced by a later prompt.
 * ``saved`` marks that the server has the row (RUN_STARTED arrived).
 */
export function touchConversationInList(
  prev: ConversationListItem[],
  id: string,
  patch?: {placeholderTitle?: string; updatedAt?: string; saved?: boolean},
): ConversationListItem[] {
  const now = patch?.updatedAt ?? new Date().toISOString()
  const existing = prev.find((c) => c.id === id)
  const placeholder = patch?.placeholderTitle?.trim()
  if (!existing) {
    return upsertConversationInList(prev, {
      id,
      title: placeholder || 'New conversation',
      createdAt: now,
      updatedAt: now,
      ...(patch?.saved ? {} : {unsaved: true}),
    })
  }
  const title = placeholder && isUnsetConversationTitle(existing.title) ? placeholder : existing.title
  const {unsaved, ...rest} = existing
  return upsertConversationInList(prev, {
    ...rest,
    title,
    updatedAt: now,
    ...(unsaved && !patch?.saved ? {unsaved} : {}),
  })
}

/** Set a conversation's title (generated or renamed) in place: no reordering, ``updatedAt`` unchanged. */
export function setConversationTitleInList(
  prev: ConversationListItem[],
  id: string,
  title: string,
): ConversationListItem[] {
  if (!prev.some((c) => c.id === id && c.title !== title)) return prev
  return prev.map((c) => (c.id === id ? {...c, title} : c))
}
